import { useCallback, useEffect, useRef } from "react";
import type Uppy from "@uppy/core";
import Dashboard from "@uppy/dashboard";

interface UseUppyDashboardOptions {
  uppy: Uppy | null;
  open: boolean;
  allRequiredFilled: boolean;
}

function getDashboardPlugin(uppy: Uppy | null) {
  return uppy?.getPlugin("Dashboard") ?? null;
}

function removeDashboardPlugin(uppy: Uppy | null) {
  const plugin = getDashboardPlugin(uppy);
  if (plugin) {
    uppy?.removePlugin(plugin);
  }
}

export function useUppyDashboard({
  uppy,
  open,
  allRequiredFilled,
}: UseUppyDashboardOptions) {
  const dashboardInitializedRef = useRef(false);

  const dashboardRef = useCallback(
    (element: HTMLDivElement | null) => {
      if (!element || !open || !allRequiredFilled) {
        if (dashboardInitializedRef.current) {
          removeDashboardPlugin(uppy);
          dashboardInitializedRef.current = false;
        }
        return;
      }

      if (!uppy) return;

      if (!dashboardInitializedRef.current) {
        uppy.use(Dashboard, {
          inline: true,
          target: element,
          proudlyDisplayPoweredByUppy: false,
          showRemoveButtonAfterComplete: true,
        });
        dashboardInitializedRef.current = true;
      } else {
        const plugin = getDashboardPlugin(uppy);
        plugin?.setOptions({ target: element });
      }
    },
    [uppy, open, allRequiredFilled]
  );

  useEffect(() => {
    const plugin = getDashboardPlugin(uppy);
    if (plugin) {
      plugin.setOptions({
        note: allRequiredFilled
          ? null
          : "Fill in all required attributes to enable file upload",
      });
    }
  }, [uppy, allRequiredFilled]);

  useEffect(() => {
    if (!open && dashboardInitializedRef.current) {
      removeDashboardPlugin(uppy);
      dashboardInitializedRef.current = false;
    }
  }, [open, uppy]);

  return dashboardRef;
}
